/**
 * Réduit une photo avant de l'envoyer à la lecture IA.
 *
 * C'est ici que ça compte le plus : le client photographie son passeport avec
 * son téléphone, et une photo d'iPhone pèse couramment 4 à 8 Mo. Elle partait
 * telle quelle, encodée en base64 — ce qui la gonfle encore d'un tiers — vers un
 * proxy qui refuse au-delà de sa limite. La lecture échouait donc chez le
 * client, sans que rien dans l'écran ne dise pourquoi.
 *
 * Ce n'est pas une perte de qualité : le texte d'un passeport se lit très bien à
 * 2000 pixels de côté long. On envoyait des pixels que la lecture n'exploite
 * pas, en faisant patienter quelqu'un sur son forfait mobile.
 *
 * ⚠ Jumeau de `apps/console/src/lib/shrinkForExtraction.ts`, qui fait la même
 * réduction pour le remplissage côté consultant. Les deux applications sont des
 * paquets séparés et ne peuvent pas partager un module DOM sans en créer un
 * troisième ; si tu touches à la logique ici, touche à l'autre.
 *
 * Deux refus volontaires : les PDF ne sont pas touchés (les rasteriser
 * détruirait leur couche de texte), et tout format que le navigateur ne sait pas
 * décoder — un HEIC, par exemple — passe intact plutôt qu'abîmé.
 */

const COTE_MAX = 2000;
const QUALITES = [0.85, 0.72, 0.6, 0.45] as const;

/** Au-delà, on réduit. En dessous, ré-encoder ne ferait que dégrader. */
const CIBLE = 900 * 1024;

const estImageReductible = (f: File) =>
  f.type.startsWith("image/") &&
  !f.type.includes("heic") &&
  !f.type.includes("heif");

export async function shrinkImage(f: File): Promise<File> {
  if (!estImageReductible(f) || f.size <= CIBLE) return f;

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(f);
  } catch {
    return f;
  }

  const ratio = Math.min(1, COTE_MAX / Math.max(bitmap.width, bitmap.height));
  const w = Math.round(bitmap.width * ratio);
  const h = Math.round(bitmap.height * ratio);

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return f;
  // Fond blanc : une image transparente aplatie en JPEG virerait au noir.
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(bitmap, 0, 0, w, h);
  bitmap.close?.();

  const dernier = QUALITES[QUALITES.length - 1];
  for (const q of QUALITES) {
    const blob = await new Promise<Blob | null>((res) =>
      canvas.toBlob(res, "image/jpeg", q),
    );
    if (!blob) break;
    if (blob.size <= CIBLE || q === dernier) {
      if (blob.size >= f.size) return f;
      return new File([blob], f.name.replace(/\.[^.]+$/, "") + ".jpg", {
        type: "image/jpeg",
        lastModified: f.lastModified,
      });
    }
  }
  return f;
}
